import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate as formatDateOnly } from "@ui/shared";
import { useRenewal } from "./RenewalContext";
import { SDA_DISCLAIMER } from "./renewalCopy";
import { Card } from "./renewalUi";

// Inline SVGs rather than an icon package — the app has no icon library and
// draws its icons this way (see the checkmarks in PolicyRenewal / SDA).
// 24x24 outline, currentColor, so each inherits its tile's text colour.
const IconRenew = (
  <svg
    className="h-6 w-6"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const IconMoveOut = (
  <svg
    className="h-6 w-6"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

const IconNotSure = (
  <svg
    className="h-6 w-6"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ChevronRight = (
  <svg
    className="h-6 w-6"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const OPTIONS = [
  {
    id: "renew",
    icon: IconRenew,
    tone: "bg-success/10 text-success",
    title: "Renew My Lease",
    subtitle: "I'd like to stay and renew my lease",
  },
  {
    id: "move-out",
    icon: IconMoveOut,
    tone: "bg-danger/10 text-danger",
    title: "I'm Moving Out",
    subtitle: "I plan to move out at the end of my lease",
  },
  {
    id: "not-sure",
    icon: IconNotSure,
    tone: "bg-warning/10 text-warning",
    title: "I'm Not Sure Yet",
    subtitle: "I'd like to see my options first",
  },
];

/**
 * Renewal-concierge entry point. Shows who/where/when, then routes into one
 * of the three flows.
 *
 * "Your lease ends" uses the CURRENT term's end date — nextTerm is the
 * upcoming (unsigned) term, so its dates describe the renewal being offered,
 * not the lease the tenant is finishing.
 */
const RenewalLanding: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { policy, termUuid, update } = useRenewal();

  const leaseEnd =
    policy?.currentTerm?.endsOn ?? policy?.endsOn ?? null;
  const unit = policy?.unit;
  const address = [unit?.property?.address, unit?.name]
    .filter(Boolean)
    .join(", ");

  const choose = (intent: string) => {
    update({ intent: intent as any });
    navigate(`/renewal/${termUuid}/${intent}`);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-primary">
        Hi, {user?.firstName || "there"}!
      </h1>
      {leaseEnd && (
        <p className="mt-1 text-sm text-gray-600">
          Your lease ends{" "}
          <span className="font-semibold text-primary">
            {formatDateOnly(leaseEnd, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </p>
      )}
      {address && (
        <p className="mt-2 text-sm text-gray-600">📍 {address}</p>
      )}

      <hr className="my-6 border-gray-200" />

      <h2 className="text-lg font-bold text-primary">
        What are your plans at the end of your current lease?
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        Takes about 2 minutes · Helps you &amp; your PM avoid expensive
        surprises
      </p>

      <div className="mt-5 space-y-3">
        {OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => choose(opt.id)}
            className="flex w-full cursor-pointer items-center gap-4 rounded-xl bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md"
          >
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${opt.tone}`}
            >
              {opt.icon}
            </span>
            <span className="flex-1">
              <span className="block font-semibold text-primary">
                {opt.title}
              </span>
              <span className="block text-sm text-gray-600">
                {opt.subtitle}
              </span>
            </span>
            <span className="shrink-0 text-gray-400">{ChevronRight}</span>
          </button>
        ))}
      </div>

      <Card className="mt-6 bg-sky-50">
        <p className="mb-2 text-sm font-semibold text-sky-900">
          {SDA_DISCLAIMER.title}
        </p>
        <p className="text-sm text-sky-900">{SDA_DISCLAIMER.body}</p>
      </Card>
    </div>
  );
};

export default RenewalLanding;
